import { PageProps } from 'libs/contentful/queries/page/props';
import { isInternalLink, isEnrollmentUrl, resolveAnonIdInUrl, ANON_ID_TOKEN } from 'utils/linkHelpers';

interface TransformLinksOptions {
  searchParams?: URLSearchParams;
  subdomain?: string;
  enrollmentUrl?: string;
  deviceId?: string;
}

const SUBDOMAIN_PREFIX_STRIPPING = new Set(['enterprise', 'careers']);

/**
 * Article-like __typenames whose entries expose a bare `slug` field (rather than a
 * nested `internalPage`). Components such as ArticleGrid wrap this `slug` in
 * `internalPage` at render time, so we strip the subdomain prefix here so the
 * rendered link matches the post-rewrite route on the subdomain.
 */
const ARTICLE_SLUG_TYPENAMES = new Set(['RelatedArticle', 'PageBlogPostFlexible', 'BlogPostPage']);

/**
 * Removes a subdomain prefix from relative URLs when on that subdomain.
 * For example, removes /careers/ prefix when on the careers subdomain.
 * Absolute URLs are left unchanged.
 */
function removeSubdomainPrefix(url: string, subdomain: string): string {
  if (url.startsWith(`/${subdomain}/`)) {
    return url.substring(`/${subdomain}`.length);
  }
  return url;
}

/**
 * Strips a leading `${subdomain}/` from a slug if present, leaving slugs without the
 * prefix unchanged.
 */
function stripSubdomainSlugPrefix(slug: string, subdomain: string): string {
  return slug.startsWith(`${subdomain}/`) ? slug.substring(`${subdomain}/`.length) : slug;
}

/**
 * Read-only, short-circuiting scan for the `[anon_id]` OneLink token anywhere in the
 * page graph. Reuses the same `ANON_ID_TOKEN` substring as resolveAnonIdInUrl so the
 * gate and the resolver never disagree on what counts as a placeholder (the substring
 * matches the literal and percent-encoded forms alike). The WeakSet keeps the scan
 * bounded on shared references.
 */
function containsAnonIdToken(value: unknown, seen: WeakSet<object> = new WeakSet()): boolean {
  if (typeof value === 'string') {
    return value.includes(ANON_ID_TOKEN);
  }

  if (value === null || typeof value !== 'object' || value instanceof Date) {
    return false;
  }

  if (seen.has(value)) {
    return false;
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.some(item => containsAnonIdToken(item, seen));
  }

  if (value instanceof Map) {
    for (const val of value.values()) {
      if (containsAnonIdToken(val, seen)) {
        return true;
      }
    }
    return false;
  }

  return Object.values(value).some(val => containsAnonIdToken(val, seen));
}

/**
 * Recursively transforms all links in a page's data to include query parameters
 * This ensures links are complete when they reach components
 */
export function transformPageLinks<T extends PageProps>(page: T, options: TransformLinksOptions = {}): T {
  const { searchParams, subdomain, deviceId } = options;

  // An empty URLSearchParams is still truthy. transformPageWithSearchParams always
  // hands us one (empty on the no-query-string majority of traffic), so without the
  // size check this early-out never fired through the real call path and every page
  // deep-cloned its entire module graph per request to append zero params - a hot-path
  // event-loop cost with no effect on output. Treat empty params the same as absent,
  // which is the semantics the `!searchParams` branch already encoded.
  const hasSearchParams = !!searchParams && searchParams.size > 0;

  // The walk also resolves the `[anon_id]` OneLink placeholder, but whether a page
  // carries that token is content-derived, not request-derived. Gate on the token
  // being present rather than on the device id: the proxy sets a device id on nearly
  // every request, so keying the walk off it would deep-clone the whole module graph
  // on all traffic and undo the empty-params fast path above. The scan is read-only
  // and short-circuits, so only pages that carry the placeholder pay for the clone.
  if (!hasSearchParams && !SUBDOMAIN_PREFIX_STRIPPING.has(subdomain ?? '') && !containsAnonIdToken(page)) {
    return page;
  }

  // Transform links in all modules, following the same pattern as replaceAllDynamicTokens
  return transformAllUrls(page, searchParams, subdomain, deviceId);
}

/**
 * Recursively transform all URLs in data, following the same pattern as replaceAllDynamicTokens
 */
export const transformAllUrls = <T extends PageProps>(
  data: T,
  searchParams?: URLSearchParams,
  subdomain?: string,
  deviceId?: string,
): T => {
  const processedObjects = new WeakMap<object, unknown>();

  const processValue = (value: unknown): unknown => {
    // Handle null/undefined
    if (value == null) {
      return value;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      const cachedArray = processedObjects.get(value);
      if (cachedArray) {
        return cachedArray;
      }

      const transformedArray: unknown[] = [];
      processedObjects.set(value, transformedArray);

      value.forEach((item, index) => {
        transformedArray[index] = processValue(item);
      });

      return transformedArray;
    }

    // Handle objects (recursively process all properties)
    if (typeof value === 'object') {
      const cachedObject = processedObjects.get(value);
      if (cachedObject) {
        return cachedObject;
      }

      // Handle Map instances
      if (value instanceof Map) {
        const clonedMap = new Map();
        processedObjects.set(value, clonedMap);

        value.forEach((val, key) => {
          clonedMap.set(key, processValue(val));
        });
        return clonedMap;
      }

      // Handle Date instances
      if (value instanceof Date) {
        return new Date(value.getTime());
      }

      let transformedValue: object = value;

      // If this object has URL or page reference properties, transform them first.
      if (hasTransformableUrl(transformedValue)) {
        const urlObject =
          isEnrollmentUrl(transformedValue.url) && data.enrollmentUrl
            ? { ...transformedValue, url: mergeQueryParams(data.enrollmentUrl, transformedValue.url) }
            : transformedValue;

        transformedValue = transformUrl(urlObject, searchParams, subdomain, deviceId);
      }

      if (hasTransformablePageReference(transformedValue)) {
        transformedValue = transformPageReference(transformedValue, searchParams, subdomain);
      }

      if (hasTransformableArticleSlug(transformedValue)) {
        transformedValue = transformArticleSlug(transformedValue, subdomain);
      }

      // Handle plain objects
      const result: Record<string, unknown> = {};
      processedObjects.set(value, result);

      for (const [key, val] of Object.entries(transformedValue)) {
        result[key] = processValue(val);
      }
      return result;
    }

    // Return primitive values as-is
    return value;
  };

  return processValue(data) as T;
};

/**
 * Transform any object with a URL to include query parameters and handle subdomain prefix removal.
 * Returns a new object with the transformed URL.
 */
function transformUrl<T extends { url: string; [key: string]: unknown }>(
  obj: T,
  searchParams?: URLSearchParams,
  subdomain?: string,
  deviceId?: string,
): T {
  // First handle subdomain prefix removal if needed
  let processedUrl = obj.url;
  if (subdomain && SUBDOMAIN_PREFIX_STRIPPING.has(subdomain) && isInternalLink(obj.url)) {
    processedUrl = removeSubdomainPrefix(obj.url, subdomain);
  }

  // Then add query params if provided
  if (searchParams) {
    processedUrl = addQueryParamsToUrl(processedUrl, searchParams);
  }

  // Resolve the `[anon_id]` OneLink placeholder server-side so the rendered href
  // is correct with JS off / before hydration. External OneLink URLs are skipped
  // by the subdomain and query-param steps above, so this is the only pass that
  // reaches them; it no-ops on every URL without the placeholder.
  processedUrl = resolveAnonIdInUrl(processedUrl, deviceId);

  return {
    ...obj,
    url: processedUrl,
  };
}

/**
 * Merge query parameters from source URL into target URL
 * Preserves existing query parameters in target URL
 */
function mergeQueryParams(targetUrl: string, sourceUrl: string): string {
  try {
    // Parse both URLs
    const targetUrlObj = new URL(targetUrl);
    const sourceUrlObj = new URL(sourceUrl);

    // Merge query parameters from source into target
    sourceUrlObj.searchParams.forEach((value, key) => {
      targetUrlObj.searchParams.set(key, value);
    });

    return targetUrlObj.toString();
  } catch {
    // If URL parsing fails, return original target URL
    return targetUrl;
  }
}

/**
 * Add query parameters to a URL.
 * Skips external URLs and fragment-only links; merges params into internal and enrollment URLs.
 */
export function addQueryParamsToUrl(url: string, searchParams: URLSearchParams): string {
  // Skip external URLs (not chime.com or 1debit.com)
  // Only process internal links and relative paths
  if (!isInternalLink(url)) {
    return url;
  }

  // Skip fragment-only links
  if (url.startsWith('#')) {
    return url;
  }

  // For enrollment URLs, use special handling that preserves existing params
  if (isEnrollmentUrl(url)) {
    try {
      // Parse the enrollment URL
      const urlObj = new URL(url);

      // Merge searchParams into the URL
      // searchParams take priority and override existing params
      searchParams.forEach((value, key) => {
        urlObj.searchParams.set(key, value);
      });

      return urlObj.toString();
    } catch {
      // If URL parsing fails, return original
      return url;
    }
  }

  try {
    // Parse the URL
    const urlObj = url.startsWith('http') ? new URL(url) : new URL(url, 'https://chime.com');

    // Add search params to the URL
    // Custom params from the CTA take priority and will override existing params
    searchParams.forEach((value, key) => {
      urlObj.searchParams.set(key, value);
    });

    // Return the appropriate format
    if (url.startsWith('http')) {
      return urlObj.toString();
    } else {
      return urlObj.pathname + urlObj.search + urlObj.hash;
    }
  } catch {
    // If URL parsing fails, return original
    return url;
  }
}

/**
 * Replaces a set of query param keys with new values: removes those keys from the URL first, then sets the new params.
 * Use when switching between variant QSP sets so old params (e.g. from previous variant) are removed.
 */
export function replaceQueryParamsInUrl(
  url: string,
  keysToReplace: ReadonlySet<string>,
  newSearchParams: URLSearchParams,
): string {
  if (!isInternalLink(url) || url.startsWith('#')) {
    return url;
  }
  try {
    const urlObj = url.startsWith('http') ? new URL(url) : new URL(url, 'https://chime.com');
    keysToReplace.forEach(key => urlObj.searchParams.delete(key));
    newSearchParams.forEach((value, key) => urlObj.searchParams.set(key, value));
    if (url.startsWith('http')) {
      return urlObj.toString();
    }
    return urlObj.pathname + urlObj.search + urlObj.hash;
  } catch {
    return url;
  }
}

/**
 * Type representing an object with a URL property
 */
type UrlObject = { url: string } & Record<string, unknown>;

/**
 * Properties that hold an InternalPage reference. The referenced page arrives in one of
 * two shapes: a flat `{ slug }` (legacy/GraphQL adapter output) or a resolved
 * Entry-native nested `{ sys, fields: { slug } }` (migrated modules).
 */
const PAGE_REFERENCE_PROPERTIES = ['internalPage', 'resourcePage', 'resourcesInternalPage'] as const;

/**
 * An object carrying at least one transformable page reference. Each reference is either
 * a flat `{ slug }` or a resolved Entry-native `{ fields: { slug } }`; readPageRefSlug
 * normalizes the read across both.
 */
type PageRefObject = {
  internalPage?: unknown;
  resourcePage?: unknown;
  resourcesInternalPage?: unknown;
} & Record<string, unknown>;

/**
 * Reads the slug from a page reference, tolerating both the flat `{ slug }` shape and the
 * resolved Entry-native nested `{ fields: { slug } }` shape. Flat is checked first so the
 * legacy path is unchanged. Returns undefined for an unresolved link (a raw Link ref with
 * no `fields`), mirroring how components/shared/CTA/toCta.ts drops it.
 */
function readPageRefSlug(pageRef: unknown): string | undefined {
  if (pageRef == null || typeof pageRef !== 'object') return undefined;

  if ('slug' in pageRef && typeof pageRef.slug === 'string') {
    return pageRef.slug;
  }

  if (
    'fields' in pageRef &&
    pageRef.fields != null &&
    typeof pageRef.fields === 'object' &&
    'slug' in pageRef.fields &&
    typeof pageRef.fields.slug === 'string'
  ) {
    return pageRef.fields.slug;
  }

  return undefined;
}

/**
 * Returns a shallow copy of a page reference with `slug` replaced, written back to the same
 * location it was read from (flat `.slug` vs nested `.fields.slug`) so the Entry-native
 * renderer - which reads `page.fields.slug` - sees the decorated value.
 */
function writePageRefSlug(pageRef: object, slug: string): object {
  // Flat first, mirroring readPageRefSlug, so the slug is written back to the same location
  // it was read from.
  if ('slug' in pageRef && typeof pageRef.slug === 'string') {
    return { ...pageRef, slug };
  }

  // Otherwise a resolved Entry-native `{ fields: { slug } }` ref - the only other shape
  // readPageRefSlug yields a slug from, so the only other shape that reaches this write.
  const fields =
    'fields' in pageRef && pageRef.fields != null && typeof pageRef.fields === 'object' ? pageRef.fields : {};
  return { ...pageRef, fields: { ...fields, slug } };
}

/**
 * Type guard to check if an object has a transformable URL
 */
function hasTransformableUrl(obj: unknown): obj is UrlObject {
  if (obj === null || typeof obj !== 'object') return false;

  const objWithUrl = obj as Record<string, unknown>;
  return 'url' in objWithUrl && typeof objWithUrl.url === 'string';
}

/**
 * Type guard to check if an object has a transformable page reference
 */
function hasTransformablePageReference(obj: unknown): obj is PageRefObject {
  if (obj === null || typeof obj !== 'object') return false;

  const objWithPage = obj as Record<string, unknown>;

  return PAGE_REFERENCE_PROPERTIES.some(prop => readPageRefSlug(objWithPage[prop]) !== undefined);
}

/**
 * Type representing an article-like object with a bare `slug` field.
 * ArticleGrid wraps this slug in `internalPage` at render time, so the slug must be
 * pre-stripped here for subdomain-prefixed slugs to produce correct URLs.
 */
type ArticleSlugObject = { __typename: string; slug: string } & Record<string, unknown>;

/**
 * Type guard for article-like objects that carry a bare `slug` field eligible for
 * subdomain-prefix stripping.
 */
function hasTransformableArticleSlug(obj: unknown): obj is ArticleSlugObject {
  if (obj === null || typeof obj !== 'object') return false;

  const candidate = obj as Record<string, unknown>;
  return (
    typeof candidate.__typename === 'string' &&
    ARTICLE_SLUG_TYPENAMES.has(candidate.__typename) &&
    typeof candidate.slug === 'string'
  );
}

/**
 * Strips the active subdomain prefix from an article-like object's bare `slug` so
 * downstream Link rendering produces a path that matches the proxy's rewritten route.
 */
function transformArticleSlug<T extends ArticleSlugObject>(obj: T, subdomain?: string): T {
  if (!subdomain || !SUBDOMAIN_PREFIX_STRIPPING.has(subdomain)) {
    return obj;
  }

  const stripped = stripSubdomainSlugPrefix(obj.slug, subdomain);
  if (stripped === obj.slug) {
    return obj;
  }

  return { ...obj, slug: stripped };
}

/**
 * Transform any object with page references to handle enterprise prefix and query parameters
 * Returns a new object with the transformed page reference
 */
function transformPageReference<T extends Record<string, unknown>>(
  obj: T,
  searchParams?: URLSearchParams,
  subdomain?: string,
): T {
  // Find the first page-reference property whose slug is readable (flat or nested).
  const pageProperty = PAGE_REFERENCE_PROPERTIES.find(prop => readPageRefSlug(obj[prop]) !== undefined);
  if (!pageProperty) return obj;

  const pageObj = obj[pageProperty];
  if (pageObj == null || typeof pageObj !== 'object') return obj;

  let processedSlug = readPageRefSlug(pageObj);
  if (processedSlug === undefined) return obj;

  // Handle subdomain prefix removal if needed
  if (subdomain && SUBDOMAIN_PREFIX_STRIPPING.has(subdomain) && processedSlug.startsWith(`${subdomain}/`)) {
    processedSlug = processedSlug.substring(`${subdomain}/`.length);
  }

  // Add query params if provided
  if (searchParams && searchParams.toString()) {
    const separator = processedSlug.includes('?') ? '&' : '?';
    processedSlug = processedSlug + separator + searchParams.toString();
  }

  return {
    ...obj,
    [pageProperty]: writePageRefSlug(pageObj, processedSlug),
  };
}
